import { Bell, CalendarRange } from "lucide-react";
import { Card, Stat, fmtDate } from "./ui";

const STEPS = ["Request", "Quotation", "Accept", "Pay", "Activate", "Invite dentists", "Renew"];

function stepIndex(org) {
  const c = org.contract;
  if (c && ["active", "grace", "scheduled"].includes(c.status)) return org.members.length ? 6 : 5;
  if (org.status === "Awaiting payment") return 3;
  if (org.quotes.some((q) => q.status === "sent")) return 2;
  return org.quotes.length ? 1 : 0;
}

function Banner({ org }) {
  const c = org.contract;
  if (!c) return null;
  let msg = null;
  if (c.status === "active" && c.days_left <= 60) msg = `Your contract ends on ${fmtDate(c.end)} (in ${c.days_left} days). DentiCision will send a renewal quotation — renew to keep all licenses active.`;
  if (c.status === "grace") msg = `Your contract ended on ${fmtDate(c.end)}. Grace period until ${fmtDate(c.grace_until)} — renew to avoid suspension.`;
  if (c.status === "suspended") msg = "Institutional access is suspended. Dentists keep their accounts and saved cases. Contact DentiCision to renew.";
  return msg && <div className="rounded-xl border border-amber-300 bg-amber-50 px-5 py-3 text-sm text-amber-900 flex gap-2 items-center" data-testid="org-banner"><Bell className="w-4 h-4 shrink-0" /> {msg}</div>;
}

export function OrgOverview({ org }) {
  const c = org.contract;
  const idx = stepIndex(org);
  return (
    <div className="space-y-5" data-testid="org-overview">
      <Banner org={org} />
      <div className="flex flex-wrap gap-2" data-testid="org-steps">
        {STEPS.map((s, i) => (
          <div key={s} className={`flex-1 min-w-[110px] rounded-xl border p-3 text-xs font-semibold ${i < idx ? "bg-emerald-50 border-emerald-200 text-emerald-700" : i === idx ? "bg-[#0B2545] border-[#0B2545] text-white" : "bg-white border-slate-200 text-slate-400"}`}>{i + 1}. {s}</div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Status" value={org.status} testid="org-stat-status" tone={["Active", "Complimentary", "Lifetime"].includes(org.status) ? "text-emerald-600" : "text-[#0B2545]"} />
        <Stat label="Licenses" value={c ? `${org.seats.active + org.seats.invited} / ${org.seats.licenses}` : "—"} sub={c ? `${org.seats.available} available` : `${org.dentists} requested`} testid="org-stat-licenses" />
        <Stat label="Contract" value={c ? (c.lifetime ? "Lifetime" : `${c.days_left >= 0 ? c.days_left : 0} days`) : "—"} sub={c ? (c.lifetime ? "Complimentary — no expiry" : `${fmtDate(c.start)} → ${fmtDate(c.end)}`) : "Not active yet"} testid="org-stat-contract" />
        <Stat label="Branches" value={org.branches || 1} sub={org.country} />
      </div>
      <Card className="text-sm text-slate-600 flex gap-3"><CalendarRange className="w-5 h-5 text-[#1C6FE3] shrink-0" />
        All dentist licenses follow one contract period. Dentists added mid-contract get access until the contract end date. Renewal keeps every dentist account and license — only the period, price and license count change.
        Reminders are sent to you and your dentists 60, 30, 14 and 3 days before expiry, then during a 14-day grace period.</Card>
    </div>
  );
}
